// ─────────────────────────────────────────────────────────────────────────────
// Geocodificación vía addresses-api.beartrackapp.com (Pelias + fallback Google).
//
// Mismo servicio y patrón de invocación que la app de repartidores
// (services/addressProvider.ts en beartrack-drivers-app): se llama DIRECTO con el
// JWT del usuario — el MISMO accessToken de localStorage que ya usa el resto de la
// API web (ver config/axios-v1-config.js). El host viejo addresses.beartrackapp.com
// quedó decomisionado (DNS inexistente → ERR_NAME_NOT_RESOLVED).
//
// Auth: header `Authorization: Bearer <accessToken>`, que pone authFetch (access vigente).
// CORS: el servicio permite el origen https://portal.beartrackapp.com, así que
// funciona desde los ambientes desplegados (QA/prod). En localhost el navegador
// manda Origin=localhost y CORS lo bloquea; para dev hay que habilitar ese origen
// en el allowlist del addresses-api (o usar un proxy de dev en src/setupProxy.js).
//
// Respuesta Pelias (GeoJSON): features[].geometry.coordinates = [lng, lat];
// properties: { id/gid, title, subtitle, label, name, street, housenumber,
//   locality, region, country, needs_resolution, source }.
// ─────────────────────────────────────────────────────────────────────────────

import { authFetch } from '../services/authFetch';

const ADDRESSES_API_URL = 'https://addresses-api.beartrackapp.com';

// Sesgo de geocodificación por país de operación. `focus.point` solo PRIORIZA
// resultados cercanos; no los restringe (no se usa boundary.circle), así una
// empresa con bodegas en todo el país sigue geocodificando direcciones fuera de
// la capital. `boundary.country` sí acota, y por eso debe seguir al país real
// del operador: con 'cl' fijo, un operador argentino no encuentra ninguna
// dirección.
const GEOCODE_DEFAULTS_BY_COUNTRY = {
  CL: { countryCode: 'cl', center: { lat: -33.45, lng: -70.65 } }, // Santiago
  AR: { countryCode: 'ar', center: { lat: -34.60, lng: -58.38 } }, // Buenos Aires
};

const DEFAULT_COUNTRY = GEOCODE_DEFAULTS_BY_COUNTRY.CL.countryCode;
const DEFAULT_FOCUS = GEOCODE_DEFAULTS_BY_COUNTRY.CL.center;

/**
 * Opciones de geocodificación ({ countryCode, center }) para un código ISO de
 * país. Los países sin entrada propia caen a Chile, que es el comportamiento
 * histórico del módulo.
 * @param {string} [code] ISO 3166-1 alpha-2, p. ej. 'CL' o 'AR'
 */
export function geocodeDefaultsFor(code) {
  return (
    GEOCODE_DEFAULTS_BY_COUNTRY[String(code || '').toUpperCase()] ||
    GEOCODE_DEFAULTS_BY_COUNTRY.CL
  );
}

async function apiGet(path, params, signal) {
  const qs = new URLSearchParams(params).toString();
  // OJO: no se setea `Origin`/`Referer` a mano — son headers prohibidos en el
  // navegador; el browser ya manda el Origin real de la página (el portal en prod).
  const res = await authFetch(`${ADDRESSES_API_URL}${path}?${qs}`, {
    method: 'GET',
    headers: { Accept: 'application/json' },
    signal,
  });
  if (!res.ok) throw new Error(`addresses-api ${res.status}`);
  return res.json();
}

// Normaliza un feature Pelias a la forma que consume el front (LocationsTab).
function mapFeature(f) {
  const coords = f?.geometry?.coordinates;
  const lng = Array.isArray(coords) ? Number(coords[0]) : NaN;
  const lat = Array.isArray(coords) ? Number(coords[1]) : NaN;
  const p = f?.properties || {};
  const street = [p.street, p.housenumber].filter(Boolean).join(' ').trim();
  const label = p.title || street || p.name || (p.label || '').split(',')[0]?.trim() || p.label || '';
  if (!label) return null;
  const needsResolution = p.needs_resolution === true;
  // Sin needs_resolution las coords deben ser válidas; con él llegan luego (resolve).
  if (!needsResolution && (!Number.isFinite(lat) || !Number.isFinite(lng))) return null;
  const subtitle =
    p.subtitle !== undefined
      ? p.subtitle
      : [p.locality, p.region, p.country].filter((x) => x && x !== label).join(', ');
  return {
    id: p.id || p.gid || `${lat},${lng}`,
    source: p.source,
    needsResolution,
    label,
    subtitle,
    lat: Number.isFinite(lat) ? lat : null,
    lng: Number.isFinite(lng) ? lng : null,
    locality: p.locality || '',
    region: p.region || '',
    // Banda de calidad que estampa addresses-api: 'exact' (calle Y número),
    // 'approximate' (la calle sí, el número no) o 'unrelated'. `mapFeature`
    // reconstruye el objeto campo por campo, así que sin copiarla acá se perdía
    // antes de llegar a la UI.
    //
    // Es lo que le permite al usuario ver de un vistazo cuál sugerencia es
    // exacta en vez de una lista donde todo parece igual de confiable — que es
    // justamente el problema que hace lenta la resolución manual.
    // OJO: `GET /v1/resolve/:placeId` NO la estampa (no hay query contra la cual
    // clasificar), así que hay que conservar la del resultado de la BÚSQUEDA.
    matchQuality: p.match_quality || null,
  };
}

async function peliasQuery(endpoint, query, { center = DEFAULT_FOCUS, countryCode = DEFAULT_COUNTRY, limit = 6, signal } = {}) {
  const text = (query || '').trim();
  if (text.length < 3) return [];
  const data = await apiGet(
    `/v1/${endpoint}`,
    {
      text,
      layers: 'address',
      size: String(limit),
      'focus.point.lat': String(center.lat),
      'focus.point.lon': String(center.lng),
      'boundary.country': countryCode,
    },
    signal,
  );
  return (data?.features || []).map(mapFeature).filter(Boolean);
}

/**
 * Autocompletado de direcciones. Devuelve un array de sugerencias
 * { id, source, needsResolution, label, subtitle, lat, lng, locality, region }.
 * Lanza ante errores de red/HTTP (el llamador decide; geocodeAddress no lanza).
 * @param {string} query
 * @param {object} [opts] { center:{lat,lng}, countryCode='cl', limit=6, signal }
 */
export function searchAddresses(query, opts = {}) {
  return peliasQuery('autocomplete', query, opts);
}

function providerFor(source, id = '') {
  if (source === 'google' || String(id).startsWith('google:')) return 'google';
  return 'pelias';
}

/**
 * Resuelve un id de sugerencia (típicamente de Google, needs_resolution) a sus
 * coordenadas reales. Devuelve { lat, lng, label, locality, region } o null.
 * Nunca lanza.
 */
export async function resolveAddress(id, { source } = {}) {
  if (!id) return null;
  try {
    const data = await apiGet(`/v1/resolve/${encodeURIComponent(id)}`, {
      provider: providerFor(source, id),
    });
    const lng = Number(data?.geometry?.coordinates?.[0]);
    const lat = Number(data?.geometry?.coordinates?.[1]);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
    const p = data?.properties || {};
    return {
      lat,
      lng,
      label: p.title || p.label || p.name || '',
      locality: p.locality || '',
      region: p.region || '',
    };
  } catch {
    return null;
  }
}

/**
 * Geocodifica un texto a { lat, lng, label } usando el mejor resultado de
 * /v1/search (resolviendo si hace falta). Devuelve null si no hay match. NUNCA
 * lanza — seguro para backfill de direcciones legadas.
 * @param {string} query
 * @param {object} [opts] { center, countryCode }
 */
export async function geocodeAddress(query, opts = {}) {
  try {
    const results = await peliasQuery('search', query, { ...opts, limit: 1 });
    if (!results.length) return null;
    const r = results[0];
    if (r.needsResolution || r.lat == null || r.lng == null) {
      const resolved = await resolveAddress(r.id, { source: r.source });
      return resolved ? { lat: resolved.lat, lng: resolved.lng, label: resolved.label || r.label } : null;
    }
    return { lat: r.lat, lng: r.lng, label: r.label };
  } catch {
    return null;
  }
}

/**
 * Normaliza las coordenadas de una dirección a [lat, lng] numéricos (formato
 * Leaflet), o null si faltan / son inválidas.
 * @param {{lat?:number|string, lng?:number|string}} addr
 * @returns {[number, number] | null}
 */
export function addressLatLng(addr) {
  if (!addr) return null;
  const { lat: rawLat, lng: rawLng } = addr;
  // Rechazar nullish/'' ANTES de coercer: Number(null)===0 y Number('')===0
  // pasarían Number.isFinite y devolverían el punto válido [0,0] (Golfo de
  // Guinea) para un formulario sin coordenadas. Se preserva el 0 legítimo.
  if (rawLat == null || rawLng == null || rawLat === '' || rawLng === '') return null;
  const lat = Number(rawLat);
  const lng = Number(rawLng);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return [lat, lng];
}

/**
 * Compone el string de geocodificación a partir de los campos de una dirección
 * operativa ({ address, municipality, state, country }).
 * @param {object} addr
 * @returns {string}
 */
export function addressToQuery(addr) {
  return [addr?.address, addr?.municipality, addr?.state, addr?.country]
    .map((p) => (p || '').trim())
    .filter(Boolean)
    .join(', ');
}
